import React from "react";
import { ExternalLink } from "lucide-react";

/**
 * Composants conformes au kit éditorial ACOSS pour l'API TDAE
 * Doc source : Indications éditoriales maquettes TDAE_2023
 *
 * ⚠️ NE PAS MODIFIER les wordings ci-dessous — ils doivent apparaître textuellement.
 */

export const URSSAF_LOGO_PATH = "/urssaf/ae-connect-banner-white.svg";

/**
 * Logo obligatoire "Auto entrepreneur Connect" — à afficher sur tous les écrans
 * du parcours utilisateur liés à l'API TDAE.
 *
 * Version bannière officielle du kit ACOSS : logo Urssaf institutionnel (bleu marine)
 * en haut + bandeau "Auto entrepreneur Connect" en bas.
 * Aspect ratio ≈ 1.71:1 (217×127).
 */
export const UrssafConnectLogo = ({ size = 40, className = "" }) => (
  <img
    src={URSSAF_LOGO_PATH}
    alt="Auto entrepreneur Connect - API Urssaf"
    height={size}
    style={{ height: size, width: "auto", display: "inline-block" }}
    className={className}
    data-testid="urssaf-connect-logo"
  />
);

/**
 * Encart obligatoire n°1 : information sur le statut auto-entrepreneur
 * avec lien de redirection vers le site institutionnel Urssaf.
 */
export const UrssafStatutInfo = () => (
  <div className="p-3 rounded-lg border text-xs space-y-1" style={{backgroundColor: "#FDFCFF", borderColor: "#E9E4F5", color: "#3B2F70"}} data-testid="urssaf-info-statut">
    <div className="font-semibold">À propos du statut d'auto-entrepreneur</div>
    <p className="text-[11px] leading-relaxed">
      Le statut d'auto-entrepreneur (aussi appelé auto-entreprise) est un régime simplifié
      qui vous permet d'exercer une activité indépendante avec des formalités allégées et
      un mode de calcul des cotisations sociales proportionnel au chiffre d'affaires
      encaissé.
    </p>
    <a
      href="https://www.auto-entrepreneur.urssaf.fr/portail/accueil/sinformer-sur-le-statut/lessentiel-du-statut.html"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium hover:underline"
      style={{color: "#5E4CBF"}}
      data-testid="urssaf-lien-statut"
    >
      En savoir plus sur le site officiel Urssaf <ExternalLink size={12} />
    </a>
  </div>
);

/**
 * Encart obligatoire n°2 : information sur l'utilisation des cotisations sociales
 * avec lien de redirection vers le site "à quoi servent les cotisations".
 */
export const UrssafCotisationsInfo = () => (
  <div className="p-3 rounded-lg border text-xs space-y-1" style={{backgroundColor: "#FDFCFF", borderColor: "#E9E4F5", color: "#3B2F70"}} data-testid="urssaf-info-cotisations">
    <div className="font-semibold">À quoi servent vos cotisations sociales ?</div>
    <p className="text-[11px] leading-relaxed">
      Vos cotisations financent votre protection sociale : maladie-maternité, retraite de
      base et complémentaire, allocations familiales, CSG-CRDS. Elles ouvrent également
      droit à la formation professionnelle.
    </p>
    <a
      href="http://www.aquoiserventlescotisations.urssaf.fr/"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium hover:underline"
      style={{color: "#5E4CBF"}}
      data-testid="urssaf-lien-cotisations"
    >
      Découvrir aquoiserventlescotisations.urssaf.fr <ExternalLink size={12} />
    </a>
  </div>
);

/**
 * Wording OBLIGATOIRE lors de la validation d'une déclaration (kit éditorial ACOSS).
 * À afficher sur l'écran de confirmation de déclaration.
 */
export const UrssafDisclaimerDeclaration = () => (
  <div className="p-3 rounded-lg text-xs leading-relaxed" style={{backgroundColor: "#EEF2FF", color: "#3730A3", border: "1px dashed #C7D2FE"}} data-testid="urssaf-disclaimer-declaration">
    <p>
      <strong>Important :</strong> l'auto-entrepreneur garde dans tous les cas la
      possibilité d'aller modifier ou ajouter des chiffres d'affaires jusqu'à la fin de la
      période d'éligibilité sur son compte Urssaf en ligne via le site ou l'application
      mobile Urssaf.
    </p>
  </div>
);

/**
 * Wording OBLIGATOIRE lors de la première création d'un mandat SEPA (kit éditorial ACOSS).
 * À afficher sur l'écran de saisie des coordonnées bancaires (première fois uniquement).
 */
export const UrssafDisclaimerMandatSepa = () => (
  <div className="p-3 rounded-lg text-xs leading-relaxed" style={{backgroundColor: "#FEF3C7", color: "#78350F", border: "1px dashed #FCD34D"}} data-testid="urssaf-disclaimer-sepa">
    <p>
      Par l'utilisation du présent service de tierce déclaration, vous autorisez votre
      Urssaf à créer un mandat de prélèvement SEPA de façon automatique lorsque vous
      transmettez de nouvelles coordonnées bancaires via ledit service. Ce mandat pourra
      être téléchargé et révoqué à tout moment par vos soins.
    </p>
  </div>
);
